'use client';

import Link from 'next/link';

interface WorkflowNavigationProps {
  currentPath: string;
  canProceed?: boolean;
  onNext?: () => void;
  onBack?: () => void;
  isLoading?: boolean;
  nextLabel?: string;
  backLabel?: string;
}

const workflowSteps = [
  { path: '/landing-page', next: '/multi-step-questionnaire' },
  { path: '/multi-step-questionnaire', next: '/lead-capture-form', back: '/landing-page' },
  { path: '/lead-capture-form', next: '/results-page', back: '/multi-step-questionnaire' },
  { path: '/results-page', back: '/landing-page' },
];

const WorkflowNavigation = ({
  currentPath,
  canProceed = true,
  onNext,
  onBack,
  isLoading = false,
  nextLabel,
  backLabel,
}: WorkflowNavigationProps) => {
  const currentStep = workflowSteps.find((s) => s.path === currentPath);

  if (!currentStep) return null;

  const defaultNextLabels: Record<string, string> = {
    '/landing-page': 'Iniciar Diagnóstico',
    '/multi-step-questionnaire': 'Continuar para Contacto',
    '/lead-capture-form': 'Ver Resultados',
  };

  const defaultBackLabels: Record<string, string> = {
    '/multi-step-questionnaire': 'Voltar ao Início',
    '/lead-capture-form': 'Voltar ao Diagnóstico',
    '/results-page': 'Novo Diagnóstico',
  };

  const resolvedNextLabel = nextLabel ?? defaultNextLabels[currentPath] ?? 'Continuar';
  const resolvedBackLabel = backLabel ?? defaultBackLabels[currentPath] ?? 'Voltar';

  // Back button — white with border, matches reference secondary button
  const backBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1.5rem',
    minHeight: '48px',
    backgroundColor: '#FFFFFF',
    color: '#374151',
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '0.875rem',
    fontWeight: 500,
    borderRadius: '5px',
    border: '1px solid #E5E7EB',
    boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  } as React.CSSProperties;

  // Next button — teal, matches reference primary teal button
  const nextBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 2rem',
    minHeight: '48px',
    backgroundColor: '#009688',
    color: '#FFFFFF',
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '0.875rem',
    fontWeight: 600,
    borderRadius: '5px',
    border: '2px solid #009688',
    boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  } as React.CSSProperties;

  const nextBtnDisabledStyle = {
    ...nextBtnStyle,
    opacity: 0.5,
    cursor: 'not-allowed',
  } as React.CSSProperties;

  return (
    <div className="flex items-center justify-between gap-4 mt-8">
      {/* Back button */}
      {currentStep.back ? (
        onBack ? (
          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            style={backBtnStyle}
            aria-label={resolvedBackLabel}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 3px 8px rgba(0,0,0,0.1)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 1px 3px rgba(0,0,0,0.07)'; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            <span className="hidden sm:inline">{resolvedBackLabel}</span>
            <span className="sm:hidden">Voltar</span>
          </button>
        ) : (
          <Link
            href={currentStep.back}
            style={backBtnStyle}
            aria-label={resolvedBackLabel}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            <span className="hidden sm:inline">{resolvedBackLabel}</span>
            <span className="sm:hidden">Voltar</span>
          </Link>
        )
      ) : (
        <div aria-hidden="true" />
      )}

      {/* Next / Submit button */}
      {currentStep.next && (
        onNext ? (
          <button
            type="button"
            onClick={onNext}
            disabled={!canProceed || isLoading}
            style={!canProceed || isLoading ? nextBtnDisabledStyle : nextBtnStyle}
            aria-label={resolvedNextLabel}
            aria-busy={isLoading}
            onMouseEnter={(e) => {
              if (canProceed && !isLoading) {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#00796B';
                (e.currentTarget as HTMLButtonElement).style.borderColor = '#00796B';
              }
            }}
            onMouseLeave={(e) => {
              if (canProceed && !isLoading) {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#009688';
                (e.currentTarget as HTMLButtonElement).style.borderColor = '#009688';
              }
            }}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.3" strokeWidth="2" />
                  <path d="M14 8A6 6 0 0 0 8 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>A processar...</span>
              </>
            ) : (
              <>
                <span>{resolvedNextLabel}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        ) : (
          <Link
            href={canProceed ? currentStep.next : '#'}
            aria-disabled={!canProceed}
            tabIndex={!canProceed ? -1 : undefined}
            style={!canProceed ? nextBtnDisabledStyle : nextBtnStyle}
            aria-label={resolvedNextLabel}
          >
            <span>{resolvedNextLabel}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        )
      )}

      {/* Results page restart */}
      {currentPath === '/results-page' && (
        <Link
          href="/landing-page"
          style={nextBtnStyle}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#00796B';
            (e.currentTarget as HTMLAnchorElement).style.borderColor = '#00796B';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#009688';
            (e.currentTarget as HTMLAnchorElement).style.borderColor = '#009688';
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M1 4v6h6M23 20v-6h-6" />
            <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
          </svg>
          <span>Novo Diagnóstico</span>
        </Link>
      )}
    </div>
  );
};

import React from 'react';
export default WorkflowNavigation;